import { Box, Button, Heading, Spinner, Text, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectComponentsProvider,
  ConnectPayouts,
  ConnectNotificationBanner,
} from "@stripe/react-connect-js";
import { useAuth } from "../../hooks/useAuth";
import { postCreateAccountSession } from "../../api/clientUser/postCreateAccountSession";
import { STRIPE_PUBLISHABLE_KEY, STRIPE_DASHBOARD } from "../../constants";
import { useShowToast } from "../../hooks/useShowToast";
import { useNavigate } from "react-router-dom";

const StripeConnectWrapper = styled.div`
  width: 100%;

  /* Override Stripe Connect primary color throughout */
  --stripe-connect-primary: #339CFF !important;

  /* Override button styles */
  button[type="button"],
  button[type="submit"],
  button[data-testid*="button"],
  a[role="button"] {
    background-color: #339CFF !important;
    border-color: #339CFF !important;
    color: white !important;
    padding: 12px 32px !important;
    font-size: 16px !important;
    font-weight: 700 !important;
    border-radius: 12px !important;
    box-shadow: 0 10px 30px rgba(51, 156, 255, 0.4) !important;
    transition: all 0.3s ease !important;
  }

  button[type="button"]:hover,
  button[type="submit"]:hover,
  button[data-testid*="button"]:hover,
  a[role="button"]:hover {
    background-color: #1a365d !important;
    border-color: #1a365d !important;
    box-shadow: 0 15px 40px rgba(51, 156, 255, 0.5) !important;
    transform: translateY(-2px) !important;
  }

  /* Override links */
  a {
    color: #339CFF !important;
  }

  a:hover {
    color: #1a365d !important;
  }

  /* Override tabs/navigation */
  [role="tab"][aria-selected="true"],
  [data-selected="true"] {
    color: #339CFF !important;
    border-color: #339CFF !important;
  }

  /* Override badges/chips */
  [data-badge],
  [class*="badge"],
  [class*="chip"] {
    background-color: rgba(51, 156, 255, 0.1) !important;
    color: #339CFF !important;
  }

  /* Override loading indicators */
  [class*="spinner"],
  [class*="loading"] {
    color: #339CFF !important;
    border-color: #339CFF !important;
  }
`;

export const Payouts = () => {
  const { accessToken, onboardingResults } = useAuth();
  const { showToast } = useShowToast();
  const navigate = useNavigate();
  const [stripeConnectInstance, setStripeConnectInstance] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeStripeConnect = async () => {
      if (!onboardingResults.stripeId || !accessToken) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch account session from our API
        const { clientSecret } = await postCreateAccountSession(
          accessToken,
          process.env.REACT_APP_KISS_API_URL as string
        );

        // Initialize Stripe Connect
        const instance = await loadConnectAndInitialize({
          publishableKey: STRIPE_PUBLISHABLE_KEY,
          fetchClientSecret: async () => clientSecret,
          appearance: {
            overlays: 'dialog',
            variables: {
              colorPrimary: "#339CFF",
              colorBackground: "#ffffff",
              colorText: "#1A202C",
              colorSecondaryText: "#718096",
              colorDanger: "#EF4444",
              colorBorder: "#E2E8F0",
              fontFamily: "system-ui, sans-serif",
              fontSizeBase: "16px",
              spacingUnit: "8px",
              borderRadius: "12px",
              buttonPrimaryColorBackground: "#339CFF",
              buttonPrimaryColorBorder: "#339CFF",
              buttonPrimaryColorText: "#ffffff",
              buttonSecondaryColorBackground: "#F7FAFC",
              buttonSecondaryColorBorder: "#E2E8F0",
              buttonSecondaryColorText: "#1A202C",
              actionPrimaryColorText: "#339CFF",
              formAccentColor: "#339CFF",
              formHighlightColorBorder: "#339CFF",
            },
          },
        });

        setStripeConnectInstance(instance);
      } catch (error) {
        console.error("Error initializing Stripe Connect:", error);
        showToast("Failed to load payouts", "error");
      } finally {
        setIsLoading(false);
      }
    };

    initializeStripeConnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingResults.stripeId, accessToken]);

  // Determine onboarding state
  const getOnboardingState = () => {
    if (!onboardingResults.stripeId) {
      return {
        title: "No Stripe Account Connected",
        message: "You need to connect a Stripe account to manage payouts.",
        buttonText: "Connect Stripe Account",
        action: () => navigate("/dashboard/start"),
      };
    }

    if (!onboardingResults.details) {
      return {
        title: "Complete Your Stripe Onboarding",
        message: "You've created a Stripe account but haven't completed the onboarding process. Please complete all required information to access payout features.",
        buttonText: "Complete Onboarding",
        action: () => navigate("/dashboard/start"),
      };
    }

    if (!onboardingResults.charges) {
      return {
        title: "Account Under Review",
        message: "Your Stripe account has been submitted and is currently under review. Payouts are temporarily disabled while Stripe verifies your information. This usually takes 1-2 business days.",
        buttonText: "Check Stripe Dashboard",
        action: () => window.open(STRIPE_DASHBOARD, "_blank"),
        isReview: true,
      };
    }

    return null;
  };

  const onboardingState = getOnboardingState();

  if (onboardingState) {
    return (
      <Box
        bg="white"
        p={8}
        borderRadius="16px"
        boxShadow="sm"
        maxW="800px"
        mx="auto"
      >
        <VStack spacing={6}>
          <Heading size="lg" color="#1A202C">
            {onboardingState.title}
          </Heading>
          <Text color="gray.600" textAlign="center" maxW="600px">
            {onboardingState.message}
          </Text>
          {onboardingState.isReview && (
            <Text fontSize="sm" color="orange.500" textAlign="center" maxW="600px">
              💡 You can monitor your account status in your Stripe Dashboard.
            </Text>
          )}
          <Button
            size="lg"
            bg="#339CFF"
            color="white"
            px={8}
            py={6}
            borderRadius="12px"
            fontSize="lg"
            fontWeight="700"
            boxShadow="0 10px 30px rgba(51, 156, 255, 0.4)"
            _hover={{
              bg: "#1a365d",
              transform: "translateY(-2px)",
              boxShadow: "0 15px 40px rgba(51, 156, 255, 0.5)",
            }}
            transition="all 0.3s"
            onClick={onboardingState.action}
          >
            {onboardingState.buttonText}
          </Button>
        </VStack>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        bg="white"
        p={8}
        borderRadius="16px"
        boxShadow="sm"
        maxW="1400px"
        mx="auto"
      >
        <VStack spacing={4}>
          <Spinner color="#339CFF" size="xl" />
          <Text color="gray.600">Loading payouts...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box maxW="1400px" mx="auto">
      <VStack spacing={6} align="stretch">
        {stripeConnectInstance && (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <ConnectNotificationBanner
              collectionOptions={{
                fields: 'currently_due',
                futureRequirements: 'omit',
              }}
            />
          </ConnectComponentsProvider>
        )}

        <Heading size="lg" color="#1A202C" fontWeight="700">
          Payouts & Balance
        </Heading>
        <Box
          bg="white"
          p={6}
          borderRadius="16px"
          boxShadow="sm"
          minH="600px"
        >
          {stripeConnectInstance ? (
            <StripeConnectWrapper>
              <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                <ConnectPayouts />
              </ConnectComponentsProvider>
            </StripeConnectWrapper>
          ) : (
            <VStack spacing={4} py={8}>
              <Spinner color="#339CFF" size="xl" />
              <Text color="gray.600">Initializing payout view...</Text>
            </VStack>
          )}
        </Box>
      </VStack>
    </Box>
  );
};
